import { useState, useRef } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import StructuredData from '@/components/base/StructuredData';
import { aboutInfo, reputations, faqs } from '@/mocks/community';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const aboutPageSchema = {
    name: '关于我们 - 海角社区',
    description: '海角社区是一个开放、包容的在线社交社区平台。了解我们的品牌故事、使命愿景和核心价值观。',
    url: window.location.origin + '/about',
  };

  const orgSchema = {
    name: '海角社区',
    url: window.location.origin,
    description: aboutInfo.intro,
    foundingDate: '2023',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '文三路478号华星时代广场',
      addressLocality: '杭州',
      addressRegion: '浙江',
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      email: aboutInfo.contact.email,
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const faqSchema = {
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      if (honeypotEl && honeypotEl.value.trim() === '') {
        formData.delete('phone_alt');
      }

      const res = await fetch('https://readdy.ai/api/form/d9qqvh4nlsngrm4lm4ag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        if (msg.toLowerCase().includes('spam') || msg.includes('form data is spam')) {
          setFormError('提交失败,请稍后再试');
        } else {
          setFormError(msg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误,请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen pt-20">
      <StructuredData type="WebPage" data={aboutPageSchema} />
      <StructuredData type="Organization" data={orgSchema} />
      <StructuredData type="FAQPage" data={faqSchema} />

      {/* Page Header */}
      <div className="bg-background-100 py-8 md:py-10">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-4">关于我们</h1>
          <p className="text-sm text-foreground-600 mt-2">了解海角社区的品牌故事与使命</p>
        </div>
      </div>

      {/* Brand Intro */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">连接你我的温暖交流平台</h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">{aboutInfo.intro}</p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{aboutInfo.mission}</p>
            </div>
            <div className="relative h-64 md:h-80 rounded-xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=warm%20cozy%20community%20gathering%20space%20with%20people%20chatting%20in%20soft%20lighting%2C%20wooden%20furniture%2C%20plants%2C%20bookshelves%2C%20friendly%20atmosphere%2C%20modern%20cafe%20style%20interior%2C%20creative%20workspace%2C%20golden%20hour%20light%20streaming%20through%20large%20windows%2C%20inviting%20and%20peaceful%20environment&width=1000&height=700&seq=about-brand-01&orientation=landscape"
                alt="海角社区温馨氛围"
                title="海角社区——温暖的交流空间"
                className="w-full h-full object-cover object-top rounded-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">我们的价值观</h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              这些信念指引着我们打造更好的社区体验
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {aboutInfo.values.map((v, idx) => (
              <div
                key={idx}
                className="bg-background-50 rounded-xl p-6 border border-background-200/60 text-center"
              >
                <div className="w-12 h-12 flex items-center justify-center mx-auto rounded-full bg-accent-100 text-accent-600 mb-4">
                  <i className={`text-xl ${['ri-heart-line', 'ri-hand-heart-line', 'ri-sun-line', 'ri-seedling-line'][idx]}`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{v.title}</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reputation */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">用户口碑</h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              听听社区成员怎么说
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {reputations.map((rep) => (
              <div
                key={rep.id}
                className="bg-background-100 rounded-xl p-6 border border-background-200/60"
              >
                <div className="flex items-center gap-3 mb-4">
                  <img src={rep.avatar} alt={rep.name} className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <div className="font-heading text-sm font-semibold text-foreground-900">{rep.name}</div>
                    <div className="text-xs text-foreground-500">{rep.role}</div>
                  </div>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">&ldquo;{rep.content}&rdquo;</p>
                <span className="text-xs text-foreground-400">{rep.joinedAt}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">常见问题</h2>
            <p className="text-sm md:text-base text-foreground-600">快速了解海角社区</p>
          </div>

          <div className="flex flex-col gap-3">
            {faqs.map((faq, idx) => (
              <details key={idx} className="group bg-background-50 rounded-xl border border-background-200/60 overflow-hidden cursor-pointer">
                <summary className="flex items-center justify-between p-5 font-heading text-sm md:text-base font-semibold text-foreground-900 list-none">
                  {faq.question}
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                </summary>
                <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Contact + Form */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">联系我们</h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                如果你有任何问题、建议或合作意向,欢迎通过以下方式联系我们,我们会尽快回复。
              </p>

              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">邮箱</div>
                    <a href={`mailto:${aboutInfo.contact.email}`} className="text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200">
                      {aboutInfo.contact.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                    <i className="ri-wechat-line"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">微信</div>
                    <span className="text-sm text-foreground-600">{aboutInfo.contact.wechat}</span>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">地址</div>
                    <span className="text-sm text-foreground-600">{aboutInfo.contact.address}</span>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-200 text-foreground-500 flex-shrink-0">
                    <i className="ri-time-line"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">工作时间</div>
                    <span className="text-sm text-foreground-600">{aboutInfo.contact.workHours}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/60">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-1">发送消息</h3>
              <p className="text-sm text-foreground-500 mb-5">我们会尽快回复你的消息</p>

              <form ref={formRef} onSubmit={handleSubmit} data-readdy-form="true" className="flex flex-col gap-4">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />

                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors duration-200"
                    placeholder="请输入你的姓名"
                  />
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors duration-200"
                    placeholder="请输入你的邮箱地址"
                  />
                </div>

                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors duration-200"
                    placeholder="消息主题（选填）"
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    消息 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors duration-200 resize-none"
                    placeholder="请输入你的消息（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多 500 个字符</p>
                </div>

                {formError && (
                  <div className="text-sm text-red-600 bg-red-50 px-4 py-2.5 rounded-lg">
                    {formError}
                  </div>
                )}

                {formStatus === 'success' && (
                  <div className="text-sm text-secondary-700 bg-secondary-50 px-4 py-2.5 rounded-lg">
                    消息已成功发送!我们会尽快与你联系。
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-primary-600 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {formStatus === 'loading' ? '发送中...' : '发送消息'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}